import React, { useState } from 'react';
import axios from 'axios';
import { Lock, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import OwnershipFooter from '../components/common/OwnershipFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PureSustain MVP Access — Security Login Page
 *
 * Sits BEFORE the existing role-selection portal. Single shared credential
 * (env-driven on the backend). Issues a 24h JWT stored in localStorage.
 */
const SecurityLogin = ({ onAuthenticated }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/mvp-auth/login`,
        { username: username.trim(), password },
      );
      // Persist token + expiry
      const expiresAt = Date.now() + (data.expires_in || 24 * 3600) * 1000;
      localStorage.setItem('mvp_token', data.token);
      localStorage.setItem('mvp_username', data.username);
      localStorage.setItem('mvp_token_expires_at', String(expiresAt));
      onAuthenticated({
        token: data.token,
        username: data.username,
        expiresAt,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : 'Invalid credentials. Please verify and try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-slate-50"
      data-testid="security-login-page"
    >
      <div className="flex-1 flex items-stretch">
        {/* Left side — form */}
        <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-12 bg-white">
          <div className="max-w-md w-full">
            {/* Logo */}
            <div className="mb-10">
              <img
                src="https://customer-assets.emergentagent.com/job_farm-to-table-135/artifacts/jfihotrx_IMG_5685.png"
                alt="PureSustain Logo"
                className="w-full h-auto"
                style={{
                  maxHeight: '180px',
                  objectFit: 'contain',
                  filter: 'contrast(1.35) brightness(1.15) saturate(1.2)',
                  imageRendering: '-webkit-optimize-contrast',
                  WebkitFontSmoothing: 'antialiased',
                }}
              />
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Secure Access
                </span>
              </div>
              <h1
                className="text-3xl font-bold text-slate-900 tracking-tight mb-2"
                style={{ fontFamily: 'Manrope' }}
              >
                PureSustain MVP Access
              </h1>
              <p className="text-sm text-slate-500 leading-relaxed">
                Secure access to the PureSustain aquaculture transparency
                platform — for authorized pilot customers, investors, advisors,
                and internal review users.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="security-login-form"
            >
              <div>
                <Label htmlFor="mvp-username" className="text-sm font-medium text-slate-700">
                  Username
                </Label>
                <Input
                  id="mvp-username"
                  data-testid="mvp-username-input"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="mt-1.5 h-11"
                  required
                />
              </div>

              <div>
                <Label htmlFor="mvp-password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <Input
                  id="mvp-password"
                  data-testid="mvp-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="mt-1.5 h-11"
                  required
                />
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200"
                  data-testid="mvp-login-error"
                >
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !username || !password}
                data-testid="mvp-login-submit"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Authenticating…
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Login
                  </>
                )}
              </Button>
            </form>

            <div className="mt-10 pt-6 border-t border-slate-200">
              <OwnershipFooter variant="inline" />
            </div>
          </div>
        </div>

        {/* Right side — brand panel */}
        <div
          className="hidden lg:flex lg:flex-1 bg-cover bg-center relative"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1765315163435-487b36141c22?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-slate-900/40" />
          <div className="relative z-10 flex items-end p-12 pb-32">
            <div className="text-white max-w-lg">
              <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>
                End-to-End Aquaculture Transparency
              </h2>
              <p className="text-sm text-slate-100 leading-relaxed">
                A supply chain transparency, traceability, and sustainability
                platform for aquaculture — built to provide visibility across
                every stage from hatchery to consumer.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityLogin;
